import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../utils/api';
import { FiSearch } from 'react-icons/fi';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const categorySlug = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [categorySlug, searchQuery, page]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = { limit: 15, page };
      if (categorySlug) params.category = categorySlug;
      if (searchQuery) params.search = searchQuery;
      
      const response = await productsAPI.getAll(params);
      const allProducts = response.data.products || [];
      setProducts(allProducts);
      setFilteredProducts(allProducts.filter(p => p.isActive !== false));
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
      setFilteredProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  const handleTabChange = (tab, catSlug = '') => {
    setSelectedTab(tab);
    if (catSlug) {
      searchParams.set('category', catSlug);
    } else {
      searchParams.delete('category');
    }
    searchParams.delete('search');
    setLocalSearchQuery('');
    setSearchParams(searchParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      searchParams.set('search', localSearchQuery.trim());
    } else {
      searchParams.delete('search');
    }
    setSearchParams(searchParams);
  };

  const getImageUrl = (product) => {
    const validImages = (product.images || []).filter(img => img && img !== null && img !== '');
    const firstImage = validImages[0];
    if (!firstImage) return null;
    
    // Construct proper image URL
    if (firstImage.startsWith('http://') || firstImage.startsWith('https://')) {
      return firstImage;
    } else if (firstImage.startsWith('/uploads/')) {
      return `http://localhost:5000${firstImage}`;
    } else {
      return `http://localhost:5000/uploads/${firstImage}`;
    }
  };

  return (
    <div className="products-page-new container">
      {/* Header */}
      <div className="products-header-new">
        <h1>Products</h1>
        <div className="products-search-bar">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search product..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="category-tabs">
        <button
          className={`tab ${selectedTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
        >
          All Products
        </button>
        <button
          className={`tab ${selectedTab === 'most-purchased' ? 'active' : ''}`}
          onClick={() => handleTabChange('most-purchased')}
        >
          Most Purchased
        </button>
        {categories.slice(0, 6).map((cat) => (
          <button
            key={cat._id}
            className={`tab ${categorySlug === cat.slug ? 'active' : ''}`}
            onClick={() => handleTabChange(cat.name.toLowerCase(), cat.slug)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Products Grid */}
      <div className="products-grid-new">
        {loading ? (
          <div className="loading-new">Loading...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="no-products-new">No products found</div>
        ) : (
          filteredProducts.map((product) => (
            <Link
              key={product._id}
              to={`/products/${product._id}`}
              className="product-card-new"
            >
              <div className="product-image-new">
                {getImageUrl(product) ? (
                  <img
                    src={getImageUrl(product)}
                    alt={product.name}
                    crossOrigin="anonymous"
                    onError={(e) => {
                      console.error('Products page image error:', getImageUrl(product));
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => console.log('✅ Products page loaded:', getImageUrl(product))}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f3f4f6',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#9ca3af"/>
                    </svg>
                    <span style={{ marginTop: '8px' }}>No Image</span>
                  </div>
                )}
              </div>
              <div className="product-info-new">
                <h3>{product.name}</h3>
                <p className="product-price-new">${product.price.toFixed(2)}</p>
                <div className="product-stats">
                  <span className="stock">Stock: {product.stock || 0}</span>
                  <span className="sold">Sold: {product.sold || 0}</span>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-bar">
          <button
            className="pagination-btn"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`pagination-btn${p === page ? ' active' : ''}`}
              onClick={() => setPage(p)}
              disabled={p === page}
            >
              {p}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Products;
